<script setup lang="ts">
import { computed } from 'vue'
import { X, CheckCircle, AlertCircle, AlertTriangle, Info } from 'lucide-vue-next'
import type { BaseComponentProps, Toast } from '@/types'
import { useUIStore } from '@/stores/ui'
import { useI18n } from '@/composables/useI18n'

interface Props extends BaseComponentProps {}

const props = withDefaults(defineProps<Props>(), {})

const uiStore = useUIStore()
const { t } = useI18n()

const toasts = computed(() => uiStore.toasts)

const visibleToasts = computed(() => toasts.value.slice(0, 5))

type ToastType = Toast['type']

// Ink toast with a 3px semantic accent on the left edge; info uses brand gold.
const accentClasses: Record<ToastType, string> = {
  success: 'border-l-success',
  error: 'border-l-error',
  warning: 'border-l-warning',
  info: 'border-l-primary',
}

const iconTileClasses: Record<ToastType, string> = {
  success: 'bg-success text-white',
  error: 'bg-error text-white',
  warning: 'bg-warning text-ink',
  info: 'bg-primary text-onPrimary',
}

const progressClasses: Record<ToastType, string> = {
  success: 'bg-success',
  error: 'bg-error',
  warning: 'bg-warning',
  info: 'bg-primary',
}

function toastType(toast: Toast): ToastType {
  return toast.type || 'info'
}

function getToastClasses(toast: Toast) {
  const base = 'relative flex w-full items-start gap-3 overflow-hidden rounded-xl border-l-[3px] bg-ink py-3.5 pl-4 pr-3 text-white shadow-[0_16px_40px_-16px_rgba(20,23,31,0.55)] sm:w-auto sm:min-w-[320px] sm:max-w-md'
  return [base, accentClasses[toastType(toast)], (toast as any).class || ''].filter(Boolean).join(' ')
}

function getIcon(toast: Toast) {
  switch (toast.type) {
    case 'success':
      return CheckCircle
    case 'error':
      return AlertCircle
    case 'warning':
      return AlertTriangle
    default:
      return Info
  }
}

function removeToast(id: string) {
  uiStore.removeToast(id)
}
</script>

<template>
  <Teleport to="body">
    <!-- The region has to clear both fixed bottom-corner elements, or at z-9999
         it sits on top of them for the toast's whole duration:
         · MobileBottomNav — a 64px `grid h-16` row at `bottom-0`, plus the iOS
           home-indicator inset;
         · the floating AI-assistant pill (App.vue) — `fixed right-5 bottom-20
           lg:bottom-7`, ~50px tall, so its top edge is ~130px up on mobile and
           ~57px up on `lg`. Below `lg` the stack is full-width and cannot dodge
           it sideways, hence `bottom-36` / `lg:bottom-28` rather than the old
           `bottom-20` / `lg:bottom-8`.
         `font-sans` is explicit because this region is teleported to <body>,
         outside App.vue's font-sans wrapper — without it the toasts inherit
         whatever font-family is set on <body> instead of Golos Text. -->
    <div
      :class="[
        'pointer-events-none fixed bottom-36 left-4 right-4 z-[9999] flex flex-col gap-3 font-sans mb-[env(safe-area-inset-bottom)] sm:left-auto sm:right-6 lg:bottom-28 lg:right-8 lg:mb-0',
        props.class,
      ]"
      role="region"
      aria-live="polite"
      :aria-label="t('aria.notifications')"
    >
      <TransitionGroup name="toast" tag="div" class="flex flex-col gap-3">
        <div
          v-for="toast in visibleToasts"
          :key="toast.id"
          :class="getToastClasses(toast)"
          class="pointer-events-auto"
        >
          <span
            class="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg"
            :class="iconTileClasses[toastType(toast)]"
            aria-hidden="true"
          >
            <component :is="getIcon(toast)" class="h-4 w-4" />
          </span>

          <div class="min-w-0 flex-1 pt-0.5">
            <p v-if="toast.title" class="text-sm font-semibold leading-snug text-white">{{ toast.title }}</p>
            <p v-if="toast.message" class="mt-0.5 text-xs leading-relaxed text-white/60">{{ toast.message }}</p>
          </div>

          <button
            type="button"
            class="-mr-1 -mt-1 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-white/60 transition hover:bg-ink-soft hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            :aria-label="t('aria.closeNotification')"
            @click="removeToast(toast.id)"
          >
            <X class="h-4 w-4" />
          </button>

          <!-- Progress bar for auto-dismiss -->
          <div
            v-if="toast.duration && toast.duration > 0"
            class="absolute bottom-0 left-0 h-0.5"
            :class="progressClasses[toastType(toast)]"
            :style="{
              width: '100%',
              animation: `toast-progress ${toast.duration}ms linear forwards`
            }"
          />
        </div>
      </TransitionGroup>
    </div>
  </Teleport>
</template>

<!-- Keyframes stay global: the inline `animation` style above cannot resolve a scoped (renamed) keyframe. -->
<style>
@keyframes toast-progress {
  from { width: 100%; }
  to { width: 0%; }
}
</style>

<style scoped>
.toast-enter-active,
.toast-leave-active {
  transition: opacity 0.25s ease-out, transform 0.25s ease-out;
}

.toast-enter-from,
.toast-leave-to {
  opacity: 0;
  transform: translateY(12px);
}

.toast-move {
  transition: transform 0.25s ease-out;
}
</style>
